<template>
  <fragment>
    <div
      v-if="xAxisCategories.length"
      class="graph-container"
    >
      <div class="sac__font-10 f-white med">
        Last 14 days
      </div>
      <highcharts
        class="runtime-chart"
        :options="chartOptions"
      />
      <div class="start-label">
        {{ minLabel }}
      </div>
      <div class="end-label">
        {{ maxLabel }}
      </div>
    </div>
    <div v-else class="no-graph-container">
      Graph data not available
    </div>
  </fragment>
</template>

<script>
import moment from 'moment';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';
import serviceMixin from '@shared/mixins/serviceMixin';

export default {
  name: 'RuntimeGraph',
  mixins: [serviceMixin, unitPreferencesMixin],
  props: {
    alertDetail: Object,
  },
  data() {
    return {
      minIndex: 0,
      maxIndex: 0,
    };
  },
  computed: {
    minLabel() {
      if (this.dateFormat === 'M/D') {
        return moment(this.xAxisCategories[this.minIndex]).format('MMM Do');
      }
      return moment(this.xAxisCategories[this.minIndex]).format('Do MMM');
    },
    maxLabel() {
      if (this.dateFormat === 'M/D') {
        return moment(this.xAxisCategories[this.maxIndex]).format('MMM Do');
      }
      return moment(this.xAxisCategories[this.maxIndex]).format('Do MMM');
    },
    runtimeData() {
      const {
        alert: {
          severity,
          type,
          boundaryValue,
        },
        graphData,
      } = this.alertDetail;
      const color = severity === 'Watching' ? '#FFAB00' : '#F83356';
      let alertCauseValue = 10;
      if (type.toLowerCase().includes('trend')) {
        alertCauseValue = boundaryValue / 60;
      }
      return graphData.map((d) => {
        const [key] = Object.keys(d);
        const value = d[key] / 3600;
        return ({
          y: value,
          color: value > alertCauseValue ? color : '#B8BCC7',
        });
      });
    },
    healthyMinMax() {
      const {
        alert: {
          healthyMin,
          healthyMax,
        },
      } = this.alertDetail;
      return {
        min: healthyMin,
        max: healthyMax,
        maxY: healthyMax + 4,
      };
    },
    xAxisCategories() {
      return this.alertDetail.graphData.map((d) => Object.keys(d)[0]);
    },
    chartOptions() {
      const {
        alert: {
          type,
        },
      } = this.alertDetail;
      const {
        dateFormat,
      } = this;
      const max = this.xAxisCategories.reduce((acc, val) => {
        let m = acc;
        const hours = val / 3600;
        if (hours > m) {
          m = Math.ceil(hours) + 4;
        }
        return m;
      }, 10);
      return {
        title: {
          text: '',
        },
        chart: {
          zoomType: 'x',
          width: null,
          height: 200,
          spacingBottom: 10,
          spacingLeft: 20,
          spacingRight: 0,
          spacingTop: 10,
          animation: false,
          backgroundColor: 'transparent',
        },
        credits: {
          enabled: false,
        },
        tooltip: {
          useHTML: true,
          nullFormat: 'Value is not available.',
          formatter() {
            return `${dateFormat === 'M/D' ? moment(this.x).format('MMM Do') : moment(this.x).format('Do MMM')}<br/>`
              + `<b>${this.y.toFixed(2)} hours</b>`;
          },
        },
        yAxis: {
          opposite: true,
          gridLineDashStyle: 'dash',
          gridLineWidth: 0.2,
          gridLineColor: '#2B3757',
          labels: {
            enabled: true,
            style: {
              fontSize: 12,
              color: 'white',
              fontWeight: 'bold',
              fontFamily: 'Gilroy, sans-serif',
            },
          },
          max,
          plotLines: type.toLowerCase().includes('trend') ? null : [
            {
              color: '#FFFFFF',
              dashStyle: 'dot',
              width: 1,
              value: this.healthyMinMax.max,
              zIndex: 5,
              label: {
                text: `${this.healthyMinMax.max} hr`,
                rotation: -90,
                style: {
                  fontSize: 10,
                  color: 'white',
                  fontWeight: 'bold',
                  fontFamily: 'Gilroy, sans-serif',
                },
                y: 10,
                x: -10,
              },
            },
          ],
          title: {
            enabled: false,
          },
        },
        xAxis: {
          categories: this.xAxisCategories,
          labels: {
            enabled: false,
          },
          events: {
            setExtremes: this.onSetExtremes,
          },
        },
        plotOptions: {
          series: {
            label: {
              connectorAllowed: true,
            },
            connectNulls: true,
            animation: {
              duration: 0,
            },
          },
        },
        series: [{
          name: '',
          type: 'column',
          pointWidth: 12,
          borderWidth: 0,
          showInLegend: false,
          color: '#232734',
          data: this.runtimeData,
          lineWidth: 3,
          lineColor: '#5A637D',
          dataLabels: {
            formatter() {
              return null;
            },
            style: {
              color: 'black',
              fontFamily: 'Gilroy, sans-serif',
            },
            borderRadius: 16,
            enabled: true,
            useHTML: true,
            y: -5,
          },
        }],
        responsive: {
          rules: [{
            condition: {
              maxWidth: 2000,
            },
          }],
        },
      };
    },
  },
  mounted() {
    if (this.xAxisCategories.length) {
      this.maxIndex = this.xAxisCategories.length - 1;
    }
  },
  methods: {
    onSetExtremes(event) {
      const { min, max } = event;
      if (min || max) {
        this.minIndex = Math.round(min);
        this.maxIndex = Math.round(max);
      } else {
        this.minIndex = 0;
        this.maxIndex = this.xAxisCategories.length - 1;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.graph-container {
  position: relative;
  .start-label {
    position: absolute;
    color: white;
    top: 210px;
    left: 30px;
    font-size: 10px;
    font-weight: 500;
  }
  .end-label {
    position: absolute;
    color: white;
    top: 210px;
    right: 40px;
    font-size: 10px;
    font-weight: 500;
  }
}
.no-graph-container {
  display: grid;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: $N50;
  border: 2px solid $N70;
}
</style>
